import type React from "react";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto } from "@/api/types";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { isDefined } from "@/helpers/util";

import { TrendBadge } from "../Badge/TrendBadge";
import { List } from "../List/List";
import { ProjectLogo } from "../ProjectLogo/ProjectLogo";
import { formatAnalyticsPercentageValue, formatAnalyticsRawValue } from "./helpers";

interface AnalyticsWidgetProjectListProps {
  projects: {
    projectId: string;
    projectName: string;
    projectLogo: ImageDto;
    score: number;
    referenceScore?: number;
  }[];
  scoreFormat?: "raw" | "percentage";
  order?: "ascending" | "descending";
}

export function AnalyticsWidgetProjectList({
  projects,
  scoreFormat = "raw",
  order = "descending",
}: AnalyticsWidgetProjectListProps): React.ReactNode {
  const resolveImage = useImageResolver();

  const sortedProjects = [...projects].sort((a, b) => {
    if (order === "ascending") {
      return a.score - b.score;
    } else {
      return b.score - a.score;
    }
  });

  return (
    <section className="w-full pt-space-sm">
      <List items={sortedProjects.slice(0, 3)} getKey={(project) => project.projectId}>
        {(project) => {
          const labelScore =
            scoreFormat === "percentage"
              ? formatAnalyticsPercentageValue(project.score)
              : `${formatAnalyticsRawValue(project.score)} score`;

          return (
            <Spread space="md">
              <Inline gap="sm" align="center" wrap={false}>
                <span className="size-icon-display">
                  <ProjectLogo src={resolveImage(project.projectLogo, "sm")} />
                </span>
                <p className="line-clamp-1 text-overline">{project.projectName}</p>
              </Inline>
              <Inline gap="sm" align="center" wrap={false}>
                <p className="text-overline-emphasis whitespace-nowrap">{labelScore}</p>
                {isDefined(project.referenceScore) && (
                  <TrendBadge
                    referenceValue={project.referenceScore}
                    value={project.score}
                    method={scoreFormat}
                    isZeroOmitted
                    shouldRoundUp={scoreFormat === "percentage"}
                  />
                )}
              </Inline>
            </Spread>
          );
        }}
      </List>
    </section>
  );
}
